<template>
  <div class="forgot-password-page">
    <div class="forgot-password-container">
      <div class="forgot-password-box">
      <div class="logo-section">
        <img :src="require('@/assets/img/OVB_logo_gray.svg')" alt="OVB" class="logo" />
        <h1 class="title">Forgot your password?</h1>
        <p v-if="!submitted" class="subtitle">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <div v-if="submitted" class="success-message">
        <h2>Check your email</h2>
        <p>
          We've sent password reset instructions to <strong>{{ email }}</strong>.
        </p>
        <p class="help-text">
          If you don't receive an email within a few minutes, check your spam folder.
        </p>
        <router-link to="/login" class="back-link">
          Back to Login
        </router-link>
      </div>

      <form v-else @submit.prevent="handleSubmit" class="forgot-password-form">
        <div class="input-wrapper">
          <label for="email" class="input-label">
            Email address
            <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            id="email"
            v-model="email"
            type="email"
            class="input-field"
            inputmode="email"
            required
            autocomplete="email"
            autocapitalize="none"
            spellcheck="false"
            :disabled="loading"
            autofocus
          />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <button type="submit" :disabled="loading" class="continue-button">
          {{ loading ? 'Sending...' : 'Send Reset Link' }}
        </button>

        <p class="back-to-login">
          <router-link to="/login" class="link">Back to Login</router-link>
        </p>
      </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ForgotPassword',
  data () {
    return {
      email: '',
      loading: false,
      error: null,
      submitted: false
    }
  },
  methods: {
    async handleSubmit () {
      this.loading = true
      this.error = null

      try {
        await axios.post('/api/auth/forgot-password', {
          email: this.email
        }, {
          withCredentials: true
        })

        this.submitted = true
      } catch (err) {
        console.error('Forgot password error:', err)
        this.error = err.response?.data?.error || 'Failed to send reset link. Please try again.'
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Auth0-inspired design - blue background, white box, no header */
.forgot-password-page {
  min-height: 100vh;
  background: #000000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.forgot-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.forgot-password-box {
  background: #ffffff;
  padding: 48px 40px;
  border-radius: 5px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  width: 100%;
  max-width: 450px;
}

.logo-section {
  text-align: center;
  margin-bottom: 32px;
}

.logo {
  width: auto;
  height: auto;
  max-height: 50px;
  max-width: 200px;
  margin-bottom: 24px;
}

.title {
  font-size: 20px;
  font-weight: 600;
  color: #000;
  margin: 0 0 26px 0;
  line-height: 1.3;
}

.subtitle {
  font-size: 14px;
  color: #65676e;
  margin: 0;
  line-height: 1.6;
}

.forgot-password-form {
  margin-top: 32px;
}

.input-wrapper {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 8px;
  line-height: 1.5;
}

.required {
  color: #d00e17;
  margin-left: 2px;
}

.input-field {
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #c9cccf;
  border-radius: 3px;
  font-size: 15px;
  line-height: 1.5;
  color: #000;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-field:hover {
  border-color: #b0b4b8;
}

.input-field:focus {
  outline: none;
  border-color: #b0b4b8;
  box-shadow: none;
}

.input-field:disabled {
  background: #f9f9f9;
  cursor: not-allowed;
  opacity: 0.6;
}

.error-message {
  background: #fee;
  color: #d00e17;
  padding: 12px 16px;
  border-radius: 3px;
  margin-bottom: 16px;
  font-size: 14px;
  border: 1px solid #fcc;
}

.success-message {
  text-align: center;
}

.success-message h2 {
  font-size: 18px;
  font-weight: 600;
  color: #0a8852;
  margin: 0 0 16px 0;
}

.success-message p {
  font-size: 14px;
  color: #65676e;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.help-text {
  font-size: 13px !important;
  color: #9196a1;
  margin-top: 16px !important;
}

.continue-button {
  width: 100%;
  padding: 12px;
  background: #ef4356;
  color: #ffffff;
  border: none;
  border-radius: 3px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  line-height: 1.5;
}

.continue-button:hover:not(:disabled) {
  background: #d63848;
}

.continue-button:disabled {
  background: #f9b2ba;
  cursor: not-allowed;
}

.back-to-login {
  text-align: center;
  margin-top: 24px;
}

.link,
.back-link {
  font-size: 14px;
  color: #0a8852;
  text-decoration: none;
  font-weight: 400;
  transition: color 0.2s;
  display: inline-block;
  margin-top: 24px;
}

.link:hover,
.back-link:hover {
  color: #086b3e;
  text-decoration: underline;
}
</style>
